/**
 * Convierte clases de Quill a estilos inline
 */
export function useQuillInlineStyles() {
    const convertQuillToInline = html => {
        if (!html) return '';

        const temp = document.createElement('div');
        temp.innerHTML = html;

        // ========== MAPEO DE CLASES QUILL A ESTILOS INLINE ==========

        const alignmentMap = {
            'ql-align-center': 'text-align: center;',
            'ql-align-right': 'text-align: right;',
            'ql-align-justify': 'text-align: justify;',
            'ql-align-left': 'text-align: left;',
        };

        const indentMap = {
            'ql-indent-1': 'padding-left: 3em;',
            'ql-indent-2': 'padding-left: 6em;',
            'ql-indent-3': 'padding-left: 9em;',
            'ql-indent-4': 'padding-left: 12em;',
            'ql-indent-5': 'padding-left: 15em;',
            'ql-indent-6': 'padding-left: 18em;',
            'ql-indent-7': 'padding-left: 21em;',
            'ql-indent-8': 'padding-left: 24em;',
            'ql-indent-9': 'padding-left: 27em;',
        };

        const fontSizeMap = {
            'ql-size-small': 'font-size: 0.75em;',
            'ql-size-large': 'font-size: 1.5em;',
            'ql-size-huge': 'font-size: 2.5em;',
        };

        const colorMap = {
            'ql-color-white': 'color: #fff;',
            'ql-color-red': 'color: #e60000;',
            'ql-color-orange': 'color: #f90;',
            'ql-color-yellow': 'color: #ff0;',
            'ql-color-green': 'color: #008a00;',
            'ql-color-blue': 'color: #06c;',
            'ql-color-purple': 'color: #93f;',
        };

        const bgColorMap = {
            'ql-bg-black': 'background-color: #000;',
            'ql-bg-red': 'background-color: #e60000;',
            'ql-bg-orange': 'background-color: #f90;',
            'ql-bg-yellow': 'background-color: #ff0;',
            'ql-bg-green': 'background-color: #008a00;',
            'ql-bg-blue': 'background-color: #06c;',
            'ql-bg-purple': 'background-color: #93f;',
        };

        const fontFamilyMap = {
            'ql-font-serif': 'font-family: Georgia, Times New Roman, serif;',
            'ql-font-monospace': 'font-family: Monaco, Courier New, monospace;',
        };

        const directionMap = {
            'ql-direction-rtl': 'direction: rtl; text-align: inherit;',
        };

        const allMaps = {
            ...alignmentMap,
            ...indentMap,
            ...fontSizeMap,
            ...colorMap,
            ...bgColorMap,
            ...fontFamilyMap,
            ...directionMap,
        };

        // ========== HELPER: aplica un string "prop: value;" al style del elemento ==========
        // Usa setProperty para que sea idempotente (sobreescribe en lugar de duplicar)
        const applyStyles = (el, stylesString) => {
            stylesString.split(';').forEach(rule => {
                const [prop, value] = rule.split(':').map(s => s.trim());
                if (prop && value) {
                    el.style.setProperty(prop, value);
                }
            });
        };

        // ========== FUNCIÓN RECURSIVA PARA PROCESAR ELEMENTOS ==========
        const processElement = element => {
            if (!element.classList) return;

            let classesToRemove = [];

            element.classList.forEach(className => {
                if (allMaps[className]) {
                    applyStyles(element, allMaps[className]);
                    classesToRemove.push(className);
                }
            });

            classesToRemove.forEach(cls => element.classList.remove(cls));

            Array.from(element.children).forEach(child => processElement(child));
        };

        Array.from(temp.children).forEach(child => processElement(child));

        // ========== APLICAR ESTILOS ESPECÍFICOS A ELEMENTOS HTML ==========

        // Negrita
        temp.querySelectorAll('strong, b').forEach(el => {
            el.style.setProperty('font-weight', 'bold');
        });

        // Cursiva
        temp.querySelectorAll('em, i').forEach(el => {
            el.style.setProperty('font-style', 'italic');
        });

        // Subrayado
        temp.querySelectorAll('u').forEach(el => {
            el.style.setProperty('text-decoration', 'underline');
        });

        // Tachado
        temp.querySelectorAll('s, strike, del').forEach(el => {
            el.style.setProperty('text-decoration', 'line-through');
        });

        // Citas (blockquote)
        temp.querySelectorAll('blockquote').forEach(el => {
            el.style.setProperty('border-left', '4px solid #ccc');
            el.style.setProperty('margin-bottom', '5px');
            el.style.setProperty('margin-top', '5px');
            el.style.setProperty('padding-left', '16px');
        });

        // Bloques de código
        temp.querySelectorAll('pre, code').forEach(el => {
            el.style.setProperty('background-color', '#f0f0f0');
            el.style.setProperty('border-radius', '3px');
            if (el.tagName === 'PRE') {
                el.style.setProperty('white-space', 'pre-wrap');
                el.style.setProperty('margin-bottom', '5px');
                el.style.setProperty('margin-top', '5px');
                el.style.setProperty('padding', '5px 10px');
            } else {
                el.style.setProperty('font-size', '85%');
                el.style.setProperty('padding', '2px 4px');
            }
        });

        // Links
        temp.querySelectorAll('a').forEach(el => {
            el.style.setProperty('text-decoration', 'underline');
            el.style.setProperty('color', '#06c');
        });

        // Encabezados
        const headingStyles = {
            H1: '2em', H2: '1.5em', H3: '1.17em',
            H4: '1em',  H5: '0.83em', H6: '0.67em',
        };
        temp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
            el.style.setProperty('font-size', headingStyles[el.tagName]);
            el.style.setProperty('margin', '0');
            el.style.setProperty('padding', '0');
        });

        // Listas
        temp.querySelectorAll('ul').forEach(el => {
            el.style.setProperty('padding-left', '1.5em');
            el.style.setProperty('margin', '0');
            el.style.setProperty('list-style-type', 'disc');
        });

        temp.querySelectorAll('ol').forEach(el => {
            el.style.setProperty('padding-left', '1.5em');
            el.style.setProperty('margin', '0');
            el.style.setProperty('list-style-type', 'decimal');
        });

        // Imágenes
        temp.querySelectorAll('img').forEach(el => {
            el.style.setProperty('max-width', '100%');
        });

        // Videos
        temp.querySelectorAll('iframe, video').forEach(el => {
            el.style.setProperty('display', 'block');
            el.style.setProperty('max-width', '100%');
        });

        return temp.innerHTML;
    };

    return {
        convertQuillToInline
    };
}